import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { getServices, getAvailability, createBooking } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CheckCircle, Ear, DotsThree } from "@phosphor-icons/react";

const todayStr = () => new Date().toISOString().slice(0, 10);

export const BookingForm = () => {
  const { t, lang } = useLang();
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(1);
  const [service, setService] = useState(null);
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [season, setSeason] = useState("summer");
  const [slotTime, setSlotTime] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    is_minor: false, parent_name: "", parent_consent: false,
    health_nickel_allergy: false, health_note: "", terms_accepted: false,
  });

  useEffect(() => { getServices().then(setServices); }, []);

  const loadAvailability = useCallback(async (d) => {
    setSlotTime(null);
    try {
      const data = await getAvailability(d);
      setSlots(data.slots);
      setSeason(data.season);
    } catch {
      setSlots([]);
    }
  }, []);

  useEffect(() => { if (date) loadAvailability(date); }, [date, loadAvailability]);

  const price = service ? service.price : 0;
  const serviceLabel = (s) => (lang === "gr" ? s.gr : s.en);

  const canProceed = service && slotTime;

  const submit = async () => {
    if (!form.name || !form.phone || !form.email) { toast.error(t.errors.required); return; }
    if (form.is_minor && !form.parent_consent) { toast.error(t.errors.consent); return; }
    if (!form.terms_accepted) { toast.error(t.errors.terms); return; }
    setSubmitting(true);
    try {
      await createBooking({
        name: form.name, phone: form.phone, email: form.email,
        service_type: service.key, date, slot_time: slotTime,
        is_minor: form.is_minor, parent_consent: form.parent_consent,
        parent_name: form.parent_name || null,
        health_nickel_allergy: form.health_nickel_allergy,
        health_note: form.health_note || null,
        terms_accepted: form.terms_accepted, lang,
      });
      setDone(true);
    } catch (e) {
      if (e?.response?.status === 409) toast.error(t.errors.taken);
      else toast.error(t.errors.generic);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setDone(false); setStep(1); setService(null); setSlotTime(null);
    setForm({ name: "", phone: "", email: "", is_minor: false, parent_name: "",
      parent_consent: false, health_nickel_allergy: false, health_note: "", terms_accepted: false });
    loadAvailability(date);
  };

  if (done) {
    return (
      <motion.div
        data-testid="booking-success"
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-[#20261E]/10 bg-white p-10 text-center shadow-[0_8px_32px_rgba(28,37,29,0.06)]"
      >
        <CheckCircle size={56} weight="fill" className="mx-auto text-[#24483A]" />
        <h3 className="mt-5 font-serif text-2xl tracking-tight text-[#20261E]">{t.success.title}</h3>
        <p className="mx-auto mt-3 max-w-md text-[#5B6053]">{t.success.body}</p>
        <Button
          data-testid="new-booking-btn"
          onClick={reset}
          className="mt-6 rounded-full bg-[#24483A] px-8 py-4 text-white hover:bg-[#163024]"
        >
          {t.success.another}
        </Button>
      </motion.div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#20261E]/10 bg-white p-6 shadow-[0_8px_32px_rgba(28,37,29,0.06)] sm:p-10">
      {/* step indicator */}
      <div className="mb-8 flex items-center gap-3">
        {[1, 2].map((n) => (
          <div key={n} className="flex items-center gap-3">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                step >= n ? "bg-[#24483A] text-white" : "bg-[#E7E1D2] text-[#8F8A79]"
              }`}
            >
              {n}
            </div>
            <span className={`text-sm ${step >= n ? "text-[#20261E]" : "text-[#8F8A79]"}`}>
              {n === 1 ? t.form.step1 : t.form.step2}
            </span>
            {n === 1 && <div className="mx-1 h-px w-8 bg-[#E7E1D2]" />}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="space-y-8"
          >
            <div>
              <p className="mb-3 text-xs uppercase tracking-[0.05em] text-[#8F8A79]">{t.form.selectService}</p>
              <div className="grid gap-3 sm:grid-cols-3">
                {services.map((s) => (
                  <button
                    key={s.key}
                    data-testid={`service-${s.key}`}
                    onClick={() => setService(s)}
                    className={`rounded-2xl border p-5 text-left transition-all duration-200 hover:-translate-y-[1px] ${
                      service?.key === s.key
                        ? "border-[#24483A] bg-[#24483A] text-white shadow-md"
                        : "border-[#20261E]/10 bg-[#F1EEE4] text-[#20261E] hover:bg-[#E7E1D2]"
                    }`}
                  >
                    {s.key === "nose" ? <DotsThree size={24} /> : <Ear size={24} />}
                    <div className="mt-3 text-sm font-semibold leading-tight">{serviceLabel(s)}</div>
                    <div className={`mt-1 text-lg font-bold ${service?.key === s.key ? "text-white" : "text-[#24483A]"}`}>
                      €{s.price}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <Label className="mb-2 block text-xs uppercase tracking-[0.05em] text-[#8F8A79]">
                  {t.form.selectDate}
                </Label>
                <Input
                  data-testid="date-input"
                  type="date"
                  min={todayStr()}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="rounded-xl border-[#20261E]/10"
                />
              </div>
              <div>
                <Label className="mb-2 block text-xs uppercase tracking-[0.05em] text-[#8F8A79]">
                  {t.form.selectSlot} · {season === "summer" ? t.form.season.summer : t.form.season.winter}
                </Label>
                <div className="flex flex-wrap gap-3">
                  {slots.length === 0 && <p className="text-sm text-[#8F8A79]">{t.form.noSlots}</p>}
                  {slots.map((s) => (
                    <button
                      key={s.time}
                      data-testid={`slot-${s.time.replace(":", "-")}`}
                      disabled={!s.available}
                      onClick={() => setSlotTime(s.time)}
                      className={`min-h-[48px] rounded-full px-5 text-sm font-medium transition-all duration-200 ${
                        !s.available
                          ? "cursor-not-allowed bg-[#F0F0F0] text-[#A3A3A3] line-through"
                          : slotTime === s.time
                          ? "bg-[#24483A] text-white shadow-md"
                          : "bg-[#E7E1D2] text-[#20261E] hover:-translate-y-[1px] hover:bg-[#DCD5C3]"
                      }`}
                    >
                      {s.time} {!s.available && `· ${t.form.booked}`}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-[#E7E1D2] pt-6">
              <div className="text-[#20261E]">
                <span className="text-sm text-[#8F8A79]">{t.form.total}: </span>
                <span data-testid="price-display" className="font-serif text-2xl font-bold text-[#24483A]">€{price}</span>
              </div>
              <Button
                data-testid="step1-next-btn"
                disabled={!canProceed}
                onClick={() => setStep(2)}
                className="rounded-full bg-[#24483A] px-8 py-4 text-white hover:bg-[#163024] disabled:opacity-40"
              >
                {t.form.next}
              </Button>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label className="mb-2 block text-sm text-[#5B6053]">{t.form.name}</Label>
                <Input data-testid="name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="rounded-xl border-[#20261E]/10" />
              </div>
              <div>
                <Label className="mb-2 block text-sm text-[#5B6053]">{t.form.phone}</Label>
                <Input data-testid="phone-input" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="rounded-xl border-[#20261E]/10" />
              </div>
              <div>
                <Label className="mb-2 block text-sm text-[#5B6053]">{t.form.email}</Label>
                <Input data-testid="email-input" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-xl border-[#20261E]/10" />
              </div>
            </div>

            <div className="rounded-xl bg-[#F1EEE4] p-5">
              <label className="flex cursor-pointer items-center gap-3">
                <Checkbox data-testid="minor-checkbox" checked={form.is_minor}
                  onCheckedChange={(v) => setForm({ ...form, is_minor: !!v })} />
                <span className="text-sm text-[#20261E]">{t.form.minor}</span>
              </label>
              {form.is_minor && (
                <div className="mt-4 space-y-4 border-t border-[#E7E1D2] pt-4">
                  <div>
                    <Label className="mb-2 block text-sm text-[#5B6053]">{t.form.parentName}</Label>
                    <Input data-testid="parent-name-input" value={form.parent_name}
                      onChange={(e) => setForm({ ...form, parent_name: e.target.value })}
                      className="rounded-xl border-[#20261E]/10 bg-white" />
                  </div>
                  <label className="flex cursor-pointer items-center gap-3">
                    <Checkbox data-testid="parent-consent-checkbox" checked={form.parent_consent}
                      onCheckedChange={(v) => setForm({ ...form, parent_consent: !!v })} />
                    <span className="text-sm text-[#20261E]">{t.form.parentConsent}</span>
                  </label>
                </div>
              )}
            </div>

            <div className="rounded-xl bg-[#F1EEE4] p-5">
              <p className="mb-3 text-xs uppercase tracking-[0.05em] text-[#8F8A79]">{t.form.healthTitle}</p>
              <label className="flex cursor-pointer items-center gap-3">
                <Checkbox data-testid="nickel-checkbox" checked={form.health_nickel_allergy}
                  onCheckedChange={(v) => setForm({ ...form, health_nickel_allergy: !!v })} />
                <span className="text-sm text-[#20261E]">{t.form.nickel}</span>
              </label>
              <Textarea data-testid="health-note-input" value={form.health_note}
                onChange={(e) => setForm({ ...form, health_note: e.target.value })}
                placeholder={t.form.healthNotePlaceholder}
                className="mt-3 rounded-xl border-[#20261E]/10 bg-white" />
              <p className="mt-2 text-xs text-[#8F8A79]">{t.form.gdpr}</p>
            </div>

            <label className="flex cursor-pointer items-center gap-3">
              <Checkbox data-testid="terms-checkbox" checked={form.terms_accepted}
                onCheckedChange={(v) => setForm({ ...form, terms_accepted: !!v })} />
              <span className="text-sm text-[#20261E]">{t.form.terms}</span>
            </label>

            <div className="flex items-center justify-between border-t border-[#E7E1D2] pt-6">
              <Button data-testid="step2-back-btn" variant="ghost" onClick={() => setStep(1)}
                className="rounded-full px-6 text-[#5B6053]">
                {t.form.back}
              </Button>
              <div className="flex items-center gap-4">
                <span className="font-serif text-xl font-bold text-[#24483A]">€{price}</span>
                <Button data-testid="submit-booking" disabled={submitting} onClick={submit}
                  className="rounded-full bg-[#24483A] px-8 py-4 text-white hover:bg-[#163024] disabled:opacity-50">
                  {submitting ? t.form.submitting : t.form.submit}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
